import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Participant } from '../types';
import { SubtitleCue } from './SubtitleParser';
import { cueBlock, cueOpacity, CUE_TRANSITION, CUE_CLASS, cueHoverCss, participantEmphasis } from './transcriptStyles';
import { prefersReducedMotion } from '../verification/verificationTheme';

interface TranscriptPanelProps {
  cues: SubtitleCue[];
  currentCueIndex: number | null;
  participants: Participant[];
  speakerAttributions: Record<string, string>;
  onSeekToCue: (cueIndex: number) => void;
  onClose?: () => void;
  // 'sidebar' is the landscape column beside the video; 'overlay' is the
  // portrait "podcast mode" that covers the whole player while the video keeps
  // playing underneath.
  variant?: 'sidebar' | 'overlay';
  // The transcript's language, picked in the panel itself rather than in a
  // menu behind the control-bar button. It is the same one setting the CC
  // menu drives, so changing it here changes the subtitles too.
  languages?: TranscriptLanguage[];
  selectedLanguage?: string;
  onChangeLanguage?: (code: string) => void;
}

export interface TranscriptLanguage {
  code: string;
  label: string;
}

// How the transcript behaves. Two genuinely different things, not a preference:
//
//   dynamic — the spoken line is pinned to the vertical centre and the
//             transcript moves past it. There is no scrollbar: the playhead is
//             the scroll control. Lines dissolve to nothing as they reach the
//             top and bottom edges.
//   static  — the whole transcript, scrollable by hand, the way a document is.
//
// Dynamic is the default because it is the one that reads as *this video,
// right now*; static is what you switch to when you want to hunt through what
// was said. Trying to scroll dynamic mode — wheel, trackpad, a finger — IS
// that switch: a gesture that silently did nothing read as the panel being
// broken, so it now drops the reader into static at the spoken line.
type TranscriptMode = 'dynamic' | 'static';

const MODE_STORAGE_KEY = 'forum.transcript.mode.v1';

// The People pill (Gavin, 11 Sep evening: "change 'names' to 'people'"). Names are HIDDEN by default (Kai, 11 Sep — decision D1): four
// long names wrapped to two rows of chips and pushed the transcript down
// (Gavin: "stupidly long names"). Hidden leaves the faces — ringed in each
// speaker's colour and lit while they talk — so who is who survives in one
// short row; the pill brings the names back for anyone who wants them.
const NAMES_STORAGE_KEY = 'forum.transcript.names.v1';

// The dissolve at the top and bottom. Cues do not stop at a hard edge; they
// thin out into the panel, which is what makes the movement read as the
// transcript flowing past a fixed point rather than a list being scrolled.
const EDGE_FADE =
  'linear-gradient(to bottom, transparent 0%, #000 20%, #000 80%, transparent 100%)';

// The Browse scrollbar sits on the LEFT (Gavin's ask) and is always drawn.
// Left comes from `direction: rtl` on the scroller, with the reel set back to
// ltr so the text is untouched. Always-drawn needs pseudo-element styling:
// macOS otherwise hides scrollbars until you are already scrolling, which is
// no help to someone looking for one. Firefox ignores the -webkit- rules and
// takes `scrollbar-color` instead; Chrome ignores `scrollbar-color` whenever
// the -webkit- rules are present, so the two do not fight.
const SCROLLER_CLASS = 'forum-transcript-scroller';
const scrollerCss = `
.${SCROLLER_CLASS}::-webkit-scrollbar { width: 8px; }
.${SCROLLER_CLASS}::-webkit-scrollbar-track { background: rgba(255,255,255,0.06); border-radius: 4px; }
.${SCROLLER_CLASS}::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.35); border-radius: 4px; }
.${SCROLLER_CLASS}::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.55); }
@supports not selector(::-webkit-scrollbar) {
  .${SCROLLER_CLASS} { scrollbar-color: rgba(255,255,255,0.35) rgba(255,255,255,0.06); }
}
${cueHoverCss}`;

let injectedScrollerStyle = false;
function ensureScrollerStyleInjected() {
  if (injectedScrollerStyle || typeof document === 'undefined') return;
  injectedScrollerStyle = true;
  const style = document.createElement('style');
  style.setAttribute('data-transcript-scroller', '');
  style.textContent = scrollerCss;
  document.head.appendChild(style);
}

const panelStyles: Record<string, React.CSSProperties> = {
  // A near-black ground, not a coloured slab. The cue blocks are the surface
  // here; the panel behind them is just the dark the rest of the player is,
  // so a white card reads as a card rather than as a lighter patch of navy.
  //
  // No backdrop-filter here: beside the video, the panel sits over the page's
  // own black, so a blur had nothing to blur — but it still made the browser
  // composite the page through it. Gavin, 21 Oct, on a Mac: with the
  // transcript open the video's blacks went grey and dotted. The overlay
  // variant below, which really is over the picture, keeps its blur.
  container: {
    backgroundColor: 'rgba(0, 0, 0, 0.72)',
    color: '#fff',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    boxSizing: 'border-box',
  },
  header: {
    padding: '12px 16px',
    fontSize: '13px',
    fontWeight: 'bold',
    color: '#aaa',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    flexShrink: 0,
  },
  participantRow: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '6px',
    padding: '10px 12px',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    flexShrink: 0,
  },
  // Names hidden: faces only, in one row.
  participantRowCompact: {
    gap: '8px',
  },
  participantChip: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    backgroundColor: 'rgba(255,255,255,0.06)',
    borderRadius: '12px',
    padding: '3px 10px 3px 3px',
    fontSize: '11px',
    color: '#ccc',
  },
  // Blocks are separated by a real gap rather than a divider — Gavin's brief
  // was "green text on a white panel and then a gap", i.e. a stack of cards,
  // not a ruled list.
  cueList: {
    flex: 1,
    overflowY: 'auto' as const,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
    padding: '10px 12px',
    direction: 'rtl' as const,
    // The scrollbar's strip is reserved in BOTH modes. Follow has no
    // scrollbar, so without this the cards were 8px wider there, rewrapped
    // their text, and every block below shifted the moment you switched —
    // enough to start the return-to-Follow glide from the wrong place.
    scrollbarGutter: 'stable',
  },
  cueText: {
    flex: 1,
  },
  // Dynamic mode: no scrollbar, nothing for a gesture to grab, and the cues
  // dissolve into the panel at both edges instead of being clipped by one.
  cueListDynamic: {
    overflowY: 'hidden' as const,
    position: 'relative' as const,
    maskImage: EDGE_FADE,
    WebkitMaskImage: EDGE_FADE,
  },
  // The column that slides. In static mode it is an ordinary flex column and
  // the transform is never set, so the same markup serves both.
  reel: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
    willChange: 'transform',
    direction: 'ltr' as const,
  },
  // Follow / Browse, directly above the speaker names as Kai asked. Deliberately
  // quiet — it is a mode switch on a panel, not a call to action.
  modeRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
    gap: '8px 4px',
    padding: '8px 12px 0',
    flexShrink: 0,
  },
  // Names hidden: the faces sit on the pill row, to the right of People
  // (Gavin, 11 Sep evening), and the row takes over the separator the
  // faces' own row used to draw.
  modeRowWithFaces: {
    paddingBottom: '10px',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
  },
  facesInline: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginLeft: '6px',
  },
  modeButton: {
    background: 'transparent',
    border: '1px solid rgba(255,255,255,0.18)',
    borderRadius: '999px',
    color: '#aaa',
    cursor: 'pointer',
    fontSize: '11px',
    padding: '4px 12px',
    lineHeight: 1.4,
  },
  languageSelect: {
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.18)',
    borderRadius: '6px',
    color: '#ddd',
    fontSize: '12px',
    fontWeight: 'normal',
    padding: '3px 6px',
    minWidth: 0,
    maxWidth: '100%',
    colorScheme: 'dark',
    cursor: 'pointer',
  },
  languageOption: {
    backgroundColor: '#1e1e1e',
    color: '#fff',
  },
  modeButtonActive: {
    background: 'rgba(255,255,255,0.16)',
    border: '1px solid rgba(255,255,255,0.28)',
    borderRadius: '999px',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '11px',
    padding: '4px 12px',
    lineHeight: 1.4,
  },
  // Overlay ("podcast mode") variants: the panel sits over the playing video,
  // so the backdrop is heavier, and the close affordance is a real labelled
  // button rather than a small ✕ glyph.
  containerOverlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.82)',
    backdropFilter: 'blur(10px)',
  },
  headerOverlay: {
    padding: '10px 12px',
    fontSize: '14px',
  },
  backButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: 'rgba(255,255,255,0.14)',
    border: '1px solid rgba(255,255,255,0.25)',
    borderRadius: '8px',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold',
    padding: '10px 14px',
    lineHeight: 1,
    minHeight: '44px',
  },
};

// The speaker's face beside each transcript line.
const CUE_AVATAR_SIZE = 30;

const avatarStyle = (size: number, color?: string): React.CSSProperties => ({
  width: size,
  height: size,
  borderRadius: '50%',
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: size * 0.55,
  fontWeight: 'bold',
  color: '#fff',
  backgroundColor: color || '#555',
  overflow: 'hidden',
});

const TranscriptPanel: React.FC<TranscriptPanelProps> = ({
  cues,
  currentCueIndex,
  participants,
  speakerAttributions,
  onSeekToCue,
  onClose,
  variant = 'sidebar',
  languages = [],
  selectedLanguage,
  onChangeLanguage,
}) => {
  ensureScrollerStyleInjected();
  const isOverlay = variant === 'overlay';
  // Read once per mount: the block style is identical for every cue, so
  // rebuilding it per row across a long transcript is wasted work.
  const blockStyle = useMemo(() => cueBlock(isOverlay), [isOverlay]);
  const transition = useMemo(() => (prefersReducedMotion() ? undefined : CUE_TRANSITION), []);
  // The fade must survive a pause and the silences between cues. useSubtitles
  // reports null whenever no cue covers the current time — at the very start,
  // in a gap, and on a paused player sitting in one — and letting that flatten
  // the panel to full opacity made the whole effect blink off exactly when
  // someone had stopped to read. So the last cue that WAS spoken keeps the
  // spotlight until another one takes it, and before anything has played the
  // top of the transcript holds it.
  const lastActiveRef = useRef(0);
  if (currentCueIndex !== null) lastActiveRef.current = currentCueIndex;
  const focusIndex = currentCueIndex ?? lastActiveRef.current;
  const [mode, setMode] = useState<TranscriptMode>(() => {
    try {
      return localStorage.getItem(MODE_STORAGE_KEY) === 'static' ? 'static' : 'dynamic';
    } catch {
      // Private windows and blocked site data throw on access, not on read.
      return 'dynamic';
    }
  });
  const isDynamic = mode === 'dynamic';
  const [showNames, setShowNames] = useState<boolean>(() => {
    try {
      return localStorage.getItem(NAMES_STORAGE_KEY) === 'shown';
    } catch {
      return false;
    }
  });
  const toggleNames = useCallback(() => {
    setShowNames((prev) => {
      try {
        localStorage.setItem(NAMES_STORAGE_KEY, prev ? 'hidden' : 'shown');
      } catch {
        // Honoured for this session even if it cannot be kept.
      }
      return !prev;
    });
  }, []);
  // Who is talking, for the participant row. Held through the gaps between
  // cues the way the card spotlight is (focusIndex), so the lit face does not
  // blink off at every pause — but nobody is lit until something has actually
  // been spoken, rather than pinning the first speaker on a player that has
  // not started.
  const hasSpokenRef = useRef(false);
  if (currentCueIndex !== null) hasSpokenRef.current = true;
  const speakingId = hasSpokenRef.current ? speakerAttributions[String(focusIndex)] ?? null : null;
  // Set when switching to Browse, consumed once by the effect below. Switching
  // hands control of the scroll position to the reader, and dropping them at
  // the top of an hour-long transcript is dropping them nowhere — they were
  // looking at the spoken line a moment ago, so that is where Browse should
  // start.
  const scrollToFocusRef = useRef(false);
  // Set when switching back to Follow, consumed once by the dynamic effect
  // below. Browse moves the transcript by SCROLLING the list; Follow moves it
  // by TRANSFORMING the reel and never scrolls. Coming back, the list is still
  // scrolled wherever Browse left it — overflow: hidden keeps a scrollTop, it
  // just stops you changing it — so the two offsets stacked and the transcript
  // landed somewhere else entirely, with nothing animating. The handover swaps
  // one for the other: the scroll becomes an equal transform (so nothing
  // moves), and only then does the reel glide to the spoken line.
  const returningFromScrollRef = useRef(false);

  const cueListRef = useRef<HTMLDivElement>(null);
  const activeCueRef = useRef<HTMLDivElement>(null);
  const reelRef = useRef<HTMLDivElement>(null);
  const [reelOffset, setReelOffset] = useState(0);
  const placedRef = useRef(false);

  const changeMode = useCallback((next: TranscriptMode) => {
    if (next === mode) return;
    if (next === 'static') {
      scrollToFocusRef.current = true;
    } else {
      // Start the reel exactly where the reader is looking. `placedRef` off
      // makes this frame a placement, not a movement.
      setReelOffset(-(cueListRef.current?.scrollTop ?? 0));
      placedRef.current = false;
      returningFromScrollRef.current = true;
    }
    setMode(next);
    try {
      localStorage.setItem(MODE_STORAGE_KEY, next);
    } catch {
      // A preference we could not persist is still a preference we can honour
      // for this session.
    }
  }, [mode]);

  // Dynamic mode's whole mechanism: measure where the spoken block sits inside
  // the reel, and translate the reel so that point lands on the panel's
  // midline. A CSS transition on the transform turns each cue change into the
  // transcript sliding by, with no scrolling involved and nothing for a user
  // gesture to fight.
  //
  // useLayoutEffect, not useEffect: the measurement has to happen before paint
  // or the reel visibly jumps to its old offset first.
  useLayoutEffect(() => {
    if (!isDynamic) return;
    const list = cueListRef.current;
    const el = activeCueRef.current;
    if (!list || !el) return;
    if (returningFromScrollRef.current) {
      // The committed transform already equals the old scroll, so zeroing the
      // scroll here leaves the picture where it was. Reading offsetTop below
      // forces a style flush at that start position, which is what lets the
      // transition that follows run from it rather than jumping.
      returningFromScrollRef.current = false;
      list.scrollTop = 0;
      const target = list.clientHeight / 2 - (el.offsetTop + el.offsetHeight / 2);
      placedRef.current = true;
      setReelOffset(target);
      return;
    }
    const target = list.clientHeight / 2 - (el.offsetTop + el.offsetHeight / 2);
    setReelOffset((prev) => (Math.abs(prev - target) < 0.5 ? prev : target));
    // The very first measurement is a placement, not a movement. Without this
    // the reel starts at 0 and glides up from wherever the transcript happens
    // to begin, which reads as the panel loading crooked and correcting itself.
    if (!placedRef.current) placedRef.current = true;
  });
  const userScrolledRef = useRef(false);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastAutoScrollTime = useRef(0);

  const handleScroll = useCallback(() => {
    // Ignore scroll events triggered by programmatic scrollIntoView
    if (Date.now() - lastAutoScrollTime.current < 600) return;
    userScrolledRef.current = true;
    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    scrollTimeoutRef.current = setTimeout(() => {
      userScrolledRef.current = false;
    }, 30000);
  }, []);

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    };
  }, []);

  // Landing on the spoken line when Browse is chosen. Jumps rather than
  // animates: the reader asked to take over, so the useful thing is to be
  // there already, not to watch a scroll. It also clears the "user has
  // scrolled" latch, because arriving here was not them scrolling.
  useLayoutEffect(() => {
    if (!scrollToFocusRef.current || isDynamic) return;
    scrollToFocusRef.current = false;
    userScrolledRef.current = false;
    lastAutoScrollTime.current = Date.now();
    activeCueRef.current?.scrollIntoView({ block: 'center', behavior: 'auto' });
  }, [isDynamic]);

  // Auto-scroll to active cue — STATIC MODE ONLY. Dynamic mode does not scroll
  // at all; it translates the reel above.
  useEffect(() => {
    if (isDynamic) return;
    if (userScrolledRef.current) return;
    if (activeCueRef.current && cueListRef.current) {
      const container = cueListRef.current;
      const el = activeCueRef.current;
      const containerRect = container.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      if (elRect.top < containerRect.top || elRect.bottom > containerRect.bottom) {
        lastAutoScrollTime.current = Date.now();
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    }
  }, [currentCueIndex]);

  // BOTH MODES RENDER EVERY CUE, and dynamic mode moves the reel rather than
  // the window.
  //
  // The obvious optimisation — render only the cues near the spoken one — is
  // what made the animation jump. Sliding the window drops a block off the top,
  // every remaining block shifts up by that block's height, and the transform
  // that re-centres the spoken line has to absorb the difference in the same
  // frame. The reel travels smoothly right up until the window moves, then
  // snaps. The offsets have to be stable for the transform to be the only
  // thing moving, and that means laying out the whole transcript once.
  //
  // The cost is real but small: a published hour is on the order of a thousand
  // plain divs, laid out once and then only transformed, which the browser
  // handles on the compositor. Static mode has rendered all of them from the
  // beginning without trouble.
  const windowed = useMemo(
    () => cues.map((cue, i) => ({ cue, i })),
    [cues],
  );

  // The reel's slide. Slower than the opacity crossfade on purpose: the cards
  // change brightness quickly so you can see WHICH line is live, and the
  // column travels slowly so the movement reads as drift rather than a jump.
  const reelTransition = useMemo(
    () => (prefersReducedMotion() ? undefined : 'transform 700ms cubic-bezier(0.33, 0, 0.15, 1)'),
    [],
  );

  const getParticipant = (participantId: string) =>
    participants.find(p => p.id === participantId);

  // Each face ringed in its speaker's colour, as the compass does; the one
  // talking glows and the rest step back. With nobody talking yet, everyone
  // is shown at full strength.
  const faceRing = (p: Participant, speaking: boolean): React.CSSProperties => ({
    borderRadius: '50%',
    boxShadow: speaking
      ? `0 0 0 2px ${p.color}, 0 0 8px 2px ${p.color}`
      : `0 0 0 2px ${p.color}`,
    transition: transition ? 'box-shadow 300ms ease' : undefined,
    flexShrink: 0,
    display: 'flex',
  });

  // A face on its own (names hidden): the name survives as a tooltip.
  const renderFace = (p: Participant, size: number) => {
    const { speaking, dimmed } = participantEmphasis(speakingId, p.id);
    return (
      <span
        key={p.id}
        title={p.name}
        aria-label={p.name}
        data-speaking={speaking ? '' : undefined}
        style={{
          ...faceRing(p, speaking),
          opacity: dimmed ? 0.6 : 1,
          transition: transition ? 'opacity 300ms ease, box-shadow 300ms ease' : undefined,
        }}
      >
        {renderAvatar(p, size)}
      </span>
    );
  };

  const renderAvatar = (p: Participant, size: number) => {
    if (p.avatar_url) {
      return (
        <img
          src={p.avatar_url}
          alt={p.name}
          style={{ ...avatarStyle(size, p.color), objectFit: 'cover' }}
        />
      );
    }
    return (
      <span style={avatarStyle(size, p.color)}>
        {p.name.charAt(0).toUpperCase()}
      </span>
    );
  };

  return (
    <div style={{ ...panelStyles.container, ...(isOverlay ? panelStyles.containerOverlay : {}) }}>
      <div
        style={{
          ...panelStyles.header,
          ...(isOverlay ? panelStyles.headerOverlay : {}),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
          <span>Transcript</span>
          {/* A native select: one tap opens the platform's own picker, which
              on a phone is the only list that is comfortable to use. */}
          {languages.length > 1 && onChangeLanguage && (
            <select
              aria-label="Transcript language"
              value={selectedLanguage}
              onChange={(e) => onChangeLanguage(e.target.value)}
              style={panelStyles.languageSelect}
            >
              {/* Options styled explicitly. Chrome on Windows draws the open
                  list on its own white ground but still inherits the select's
                  light text, so without this the list was near-white on white. */}
              {languages.map((l) => (
                <option key={l.code} value={l.code} style={panelStyles.languageOption}>{l.label}</option>
              ))}
            </select>
          )}
        </span>
        {onClose && (isOverlay ? (
          <button onClick={onClose} style={panelStyles.backButton} aria-label="Back to video">
            <span style={{ fontSize: '16px' }}>✕</span>
            <span>Back to video</span>
          </button>
        ) : (
          <button
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', color: '#aaa', cursor: 'pointer', fontSize: '16px', padding: '0 4px', lineHeight: 1 }}
            aria-label="Close transcript"
          >
            ✕
          </button>
        ))}
      </div>
      <div style={{ ...panelStyles.modeRow, ...(participants.length > 0 && !showNames ? panelStyles.modeRowWithFaces : {}) }}>
        <div style={{ display: 'flex', gap: '4px' }} role="group" aria-label="Transcript mode">
        {(['dynamic', 'static'] as TranscriptMode[]).map((m) => (
          <button
            key={m}
            onClick={() => changeMode(m)}
            aria-pressed={mode === m}
            style={mode === m ? panelStyles.modeButtonActive : panelStyles.modeButton}
            title={
              m === 'dynamic'
                ? 'Follows the video — the spoken line stays centred'
                : 'Freezes the transcript so you can scroll it'
            }
          >
            {m === 'dynamic' ? 'Follow' : 'Browse'}
          </button>
        ))}
        </div>
        {/* Not part of the Follow / Browse choice — a separate on/off, set a
            little apart so it does not read as a third mode. */}
        {participants.length > 0 && (
          <button
            onClick={toggleNames}
            aria-pressed={showNames}
            style={{
              ...(showNames ? panelStyles.modeButtonActive : panelStyles.modeButton),
              marginLeft: '8px',
            }}
            title={showNames ? "Hide people's names" : "Show people's names"}
          >
            People
          </button>
        )}
        {participants.length > 0 && !showNames && (
          <div style={panelStyles.facesInline} aria-label="Speakers">
            {participants.map((p) => renderFace(p, 22))}
          </div>
        )}
      </div>
      {participants.length > 0 && showNames && (
        <div
          style={panelStyles.participantRow}
          aria-label="Speakers"
        >
          {participants.map(p => {
            const { speaking, dimmed } = participantEmphasis(speakingId, p.id);
            const ring = faceRing(p, speaking);
            return (
              <div
                key={p.id}
                style={{
                  ...panelStyles.participantChip,
                  opacity: dimmed ? 0.6 : 1,
                  color: speaking ? '#fff' : panelStyles.participantChip.color,
                  transition: transition ? 'opacity 300ms ease' : undefined,
                }}
                data-speaking={speaking ? '' : undefined}
              >
                <span style={ring}>{renderAvatar(p, 20)}</span>
                <span>{p.name}</span>
              </div>
            );
          })}
        </div>
      )}
      {/* In overlay mode the control bar is pinned over the bottom of the
          player, so the last cues need room to clear it. */}
      <div
        ref={cueListRef}
        className={SCROLLER_CLASS}
        style={{
          ...panelStyles.cueList,
          ...(isOverlay ? { paddingBottom: '64px' } : {}),
          ...(isDynamic ? panelStyles.cueListDynamic : null),
        }}
        onScroll={isDynamic ? undefined : handleScroll}
        onWheel={isDynamic ? () => changeMode('static') : undefined}
        onTouchMove={isDynamic ? () => changeMode('static') : undefined}
      >
        <div
          ref={reelRef}
          style={
            isDynamic
              ? {
                  ...panelStyles.reel,
                  transform: `translateY(${reelOffset}px)`,
                  transition: placedRef.current ? reelTransition : undefined,
                }
              : panelStyles.reel
          }
        >
        {windowed.map(({ cue, i }) => {
          const isActive = i === focusIndex;
          const attributionId = speakerAttributions[String(i)];
          const participant = attributionId ? getParticipant(attributionId) : undefined;
          const speakerColor = participant?.color;

          return (
            <div
              key={i}
              ref={isActive ? activeCueRef : undefined}
              className={CUE_CLASS}
              data-spoken={isActive ? '' : undefined}
              style={{
                ...blockStyle,
                // Distance from the spotlit cue drives the fade. focusIndex
                // holds through pauses and gaps, so the gradient never blinks
                // off — see lastActiveRef above.
                opacity: cueOpacity(i - focusIndex),
                transition,
                borderLeftColor: speakerColor || 'transparent',
              }}
              onClick={() => {
                userScrolledRef.current = false;
                if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
                onSeekToCue(i);
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {/* 20px until Gavin, 3 Nov: "make avatars next to text bigger". */}
                {participant && renderAvatar(participant, CUE_AVATAR_SIZE)}
                {/* Cue text is always black. It used to be painted in the
                    speaker's own colour, which read well as "a conversation
                    between named people" but failed at the only job body text
                    has: those colours are chosen to be findable on the compass
                    against moving video, and at paragraph size on a panel they
                    were unreadable.

                    The colour still identifies the speaker — it just does it
                    in the 3px bar down the left edge and on the avatar, where
                    a fleck is enough. Subtitles burned over the video stay
                    white for their own reason: they are read once, at speed,
                    over moving picture. */}
                <span style={panelStyles.cueText}>{cue.text}</span>
              </div>
            </div>
          );
        })}
        </div>
      </div>
    </div>
  );
};

export default TranscriptPanel;

